import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useMemo,
} from 'react';

import { RadioGroupContext } from '../radio-group/radio-group';

import styles from './radio.scss';

type Props = RefAttributes<HTMLInputElement> &
  HTMLAttributes<HTMLInputElement> & {
    value: string;
    checked?: boolean;
    disabled?: boolean;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-radio';

export const Radio: ForwardRefExoticComponent<Props> = forwardRef<HTMLInputElement, Props>(
  ({ value = '', checked, disabled, className, dataTestId = COMPONENT_ID, ...rest }, ref) => {
    const radioGroupContext = React.useContext(RadioGroupContext);

    const isChecked = useMemo(
      () =>
        radioGroupContext?.checkedValue === null
          ? !!checked
          : radioGroupContext?.checkedValue === value.toString(),
      [checked, radioGroupContext?.checkedValue, value],
    );

    const isDisabled = useMemo(
      () => disabled || !!radioGroupContext?.disabled,
      [disabled, radioGroupContext?.disabled],
    );

    return (
      <input
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        ref={ref}
        className={classNames(styles.radio, className, {
          [styles['input-mode']]: isDisabled && isChecked,
        })}
        type="radio"
        value={value}
        checked={isChecked}
        disabled={isDisabled}
        onChange={radioGroupContext?.handleChange}
        {...rest}
      />
    );
  },
);

Radio.displayName = 'Radio';
